/**
 * Spinner — the ONE loading indicator for the whole app.
 *
 * Before this, every surface rolled its own: a ``Loader2`` icon here, a
 * blue ring-border ``<div>`` there, a ``RefreshCw`` elsewhere — and placement
 * drifted (left-corner in Scheduler, centered in the Workspace tab). That made
 * loading states feel inconsistent. Use this everywhere instead.
 *
 * Two intents, so placement is decided by MEANING, not by each caller's CSS:
 *
 *   <Spinner />                      inline icon (buttons, "Loading runs…" rows)
 *   <Spinner label="Loading runs…"/> inline icon + label, left-aligned
 *   <Spinner variant="panel"         centered in its container — for a whole
 *            label="Loading…" />     tab / section / dialog body load
 *
 * Sizes: ``sm`` (12px), ``md`` (16px, default), ``lg`` (24px). Colour is the
 * neutral ``text-gray-400`` by default (override via ``className`` on the icon
 * through ``iconClassName`` when a surface needs an accent).
 */
import React from 'react';
import { Loader2 } from 'lucide-react';

const _SIZE = { sm: 'h-3 w-3', md: 'h-4 w-4', lg: 'h-6 w-6' };
const _TEXT = { sm: 'text-[11px]', md: 'text-xs', lg: 'text-sm' };

export default function Spinner({
  variant = 'inline',          // 'inline' | 'panel'
  size = 'md',                 // 'sm' | 'md' | 'lg'
  label = '',                  // optional text beside the icon
  className = '',              // extra classes on the wrapper
  iconClassName = 'text-gray-400',
  testId,
}) {
  const sz = _SIZE[size] || _SIZE.md;
  const txt = _TEXT[size] || _TEXT.md;

  const icon = <Loader2 className={`${sz} animate-spin ${iconClassName}`} aria-hidden />;

  if (variant === 'panel') {
    // TOP-CENTERED for full tab/section/dialog loads: horizontally centered,
    // anchored near the top with a consistent ``pt-10`` — NOT vertically centered
    // in the parent. (``flex-1 items-center`` made the spinner land dead-center in
    // a full-height parent like the Workspace tab but top-ish in a shorter one —
    // the inconsistency. Top-anchored looks identical regardless of parent height.)
    return (
      <div
        className={`flex w-full items-start justify-center gap-2 px-6 pt-10 ${txt} text-gray-500 ${className}`}
        role="status"
        aria-live="polite"
        data-testid={testId}
      >
        {icon}
        {label ? <span>{label}</span> : null}
        {!label ? <span className="sr-only">Loading…</span> : null}
      </div>
    );
  }

  // Inline: icon (+ label) on one line, left-aligned, no extra padding.
  return (
    <span
      className={`inline-flex items-center gap-2 ${txt} text-gray-500 ${className}`}
      role="status"
      aria-live="polite"
      data-testid={testId}
    >
      {icon}
      {label ? <span>{label}</span> : null}
      {!label ? <span className="sr-only">Loading…</span> : null}
    </span>
  );
}
